// src/v2/components/AlertChip.jsx
//
// ALERT-CHIP-AND-TWO-CLUSTER-TOOLBAR — shared leading-toolbar alert chip +
// slide-open panel. Replaces the below-toolbar StatusBand row on Offices,
// Leases, and Market Plan (supersedes ALERTBAND-BELOW-TOOLBAR and folds in
// OFFICES-MAP-HIDDEN-COUNT-INTO-STATUSBAND).
//
// Renders a small severity-dot + count chip that LEADS the toolbar row
// (before Search), with a thin divider right after it. Chip height is
// pinned to T.toolbarCtrlH so it never changes the toolbar row height.
//
// Clicking the chip opens a panel listing each alert as one row (dot +
// summary + optional action link + dismiss ×). The panel is rendered via a
// portal to document.body — positioned from the chip's own bounding rect —
// so it can never be clipped by a toolbar row's `overflow: hidden` (do not
// reintroduce the filter/sort-clipping regression).
//
// Collapsed (panel closed) renders no panel at all — never a reserved gap
// (phantom-gap lesson: hidden = null, never an empty node). The chip itself
// is fully hidden (returns null) once it collapses, and never rendered at all
// in steady-state when no alerts have ever been active.
//
// All-clear behaviour:
//   When the active count transitions from >0 → 0 the chip switches to its
//   green "Clear" state, fires onAllClear(), holds for ~2 s, then fades out
//   over 300 ms and collapses away entirely. localStorage open/closed key is
//   reset to false on collapse so a future re-appearance opens fresh.
//
// Open/closed state persists per view via localStorage (`storageKey`).
//
// Message shape:
//   {
//     id:           string   — stable key
//     severity?:    "warn" | "urgent"  (default "warn"; urgent = red dot)
//     text:         ReactNode — one-line summary
//     actionLabel?: string    — optional "Review / Fix / Open →" link
//     onAction?:    () => void
//   }
//
// Props:
//   messages    — Array<Message>  candidate alerts, already scoped to the
//                 current view's conditional-visibility rule by the caller
//   okMessage   — { text }  shown (via the "Clear" chip's tooltip/aria-label)
//                 when there are no active alerts
//   storageKey  — localStorage key for open/closed persistence (per view)
//   testId      — data-testid prefix (default "alert-chip")
//   onAllClear  — optional () => void — called once when active count first
//                 drops to 0 (optional non-notification side effects)
//   onDismissAll — optional () => void — dismisses all current rows for the
//                 current view session; omitted for consumers that do not need
//                 the chip-level affordance

import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { T } from "../../app/tokens.js";
import { useLocalStorage } from "../../hooks/useLocalStorage.js";

const SEVERITY_DOT = {
  warn:   T.ragAmber,
  urgent: T.ragRed,
};

const HOLD_MS = 2000;
const FADE_MS = 300;

export function AlertChip({
  messages = [],
  okMessage = { text: "All clear" },
  storageKey = "ripple.alertChip.open",
  testId = "alert-chip",
  onAllClear,
  onDismissAll,
  chipLabel,
  panelHeader = null,
  reservedWidth,
  pending = false,
}) {
  const [dismissed, setDismissed] = useState(() => new Set());
  const [open, setOpen] = useLocalStorage(storageKey, false);
  const chipRef = useRef(null);
  const panelRef = useRef(null);
  const [panelRect, setPanelRect] = useState(null);

  const active = (messages ?? []).filter((m) => m && !dismissed.has(m.id));
  const isAllClear = active.length === 0;

  // Lifecycle state for the fade-out transition.
  // collapsed: true  → render nothing (null)
  // clearing:  true  → showing green all-clear chip (2 s hold phase)
  // fading:    true  → opacity tween to 0 before collapse
  const hadAlertsRef    = useRef(active.length > 0);
  const prevActiveRef   = useRef(active.length);
  const holdTimerRef    = useRef(null);
  const fadeTimerRef    = useRef(null);

  const [collapsed, setCollapsed] = useState(() => active.length === 0);
  const [clearing,  setClearing]  = useState(false);
  const [fading,    setFading]    = useState(false);

  // Detect transitions in active count.
  useEffect(() => {
    if (pending) return;
    const prev = prevActiveRef.current;
    prevActiveRef.current = active.length;

    if (active.length > 0) {
      hadAlertsRef.current = true;
      // Cancel any in-flight hold/fade timers so stale callbacks cannot
      // collapse the chip while new active alerts are present.
      clearTimeout(holdTimerRef.current);
      clearTimeout(fadeTimerRef.current);
      if (collapsed || clearing || fading) {
        setCollapsed(false);
        setClearing(false);
        setFading(false);
      }
      return;
    }

    // active.length === 0
    if (prev > 0 && hadAlertsRef.current) {
      // Transition: alerts just resolved.
      onAllClear?.();
      setClearing(true);
      setFading(false);
      clearTimeout(holdTimerRef.current);
      clearTimeout(fadeTimerRef.current);

      holdTimerRef.current = setTimeout(() => {
        setFading(true);
        fadeTimerRef.current = setTimeout(() => {
          setCollapsed(true);
          setClearing(false);
          setFading(false);
          setOpen(false); // reset localStorage key so future appearance opens fresh
        }, FADE_MS);
      }, HOLD_MS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active.length, pending]);

  // Clean up timers on unmount.
  useEffect(() => {
    return () => {
      clearTimeout(holdTimerRef.current);
      clearTimeout(fadeTimerRef.current);
    };
  }, []);

  // Auto-close the panel when the last alert clears (stale open panel).
  useEffect(() => {
    if (isAllClear && open) setOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAllClear]);

  // Position the portaled panel under the chip; recompute on open + on
  // resize/scroll while open so it tracks the chip if the layout shifts.
  useEffect(() => {
    if (!open || pending) return undefined;
    function measure() {
      if (!chipRef.current) return;
      const r = chipRef.current.getBoundingClientRect();
      setPanelRect({ top: r.bottom + 8, left: r.left, minWidth: Math.max(320, r.width) });
    }
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [open, pending]);

  // Click-away + Escape to close.
  useEffect(() => {
    if (!open) return undefined;
    function onDown(e) {
      if (chipRef.current?.contains(e.target)) return;
      if (panelRef.current?.contains(e.target)) return;
      setOpen(false);
    }
    function onKey(e) { if (e.key === "Escape") setOpen(false); }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Nothing to show — either never had alerts or fully collapsed after fade.
  if (pending) return (
    <span data-testid={`${testId}-checking`} role="status" aria-label="Checking alerts"
      style={{ width: (reservedWidth ?? T.alertChipSlotW) + 1, height: T.toolbarCtrlH,
        flexShrink: 0, boxSizing: "border-box", borderRadius: T.radiusCard, background: T.lightGray }} />
  );
  // Reserved-slot consumers must not spend a paint collapsed after resolution.
  if (collapsed && !(reservedWidth && active.length > 0)) return null;

  function handleDismiss(id) {
    setDismissed((prev) => new Set(prev).add(id));
  }

  function handleDismissAll() {
    setDismissed((prev) => new Set([
      ...prev,
      ...active.map((message) => message.id),
    ]));
    onDismissAll?.();
  }

  const dotColor = isAllClear
    ? T.greenDeep
    : (SEVERITY_DOT[active[0]?.severity] ?? T.ragAmber);

  return (
    <span
      data-testid={testId}
      style={{
        display:    "inline-flex",
        alignItems: "center",
        gap:        0,
        flexShrink: 0,
        opacity:    fading ? 0 : 1,
        transition: fading ? `opacity ${FADE_MS}ms ease` : "none",
      }}
    >
      <button
        ref={chipRef}
        type="button"
        data-testid={`${testId}-btn`}
        onClick={() => {
          // During the clearing/fading phase clicking does nothing useful.
          if (clearing || fading) return;
          setOpen((v) => !v);
        }}
        aria-expanded={open}
        aria-label={
          isAllClear
            ? (okMessage?.text ?? "All clear")
            : `${active.length} alert${active.length === 1 ? "" : "s"} - click to view`
        }
        style={{
          display:      "inline-flex",
          alignItems:   "center",
          gap:          6,
          height:       T.toolbarCtrlH,
          boxSizing:    "border-box",
          width:        reservedWidth,
          padding:      "0 10px",
          border:       `1px solid ${isAllClear ? T.ragGreenBg : "var(--v2-line)"}`,
          background:   isAllClear ? T.ragGreenBg : "var(--v2-card)",
          borderRadius: 8,
          cursor:       clearing || fading ? "default" : "pointer",
          fontSize:     T.fsSm5,
          fontWeight:   T.fwBold,
          color:        isAllClear ? T.greenDeep : "var(--v2-dark)",
        }}
      >
        <span
          aria-hidden="true"
          data-testid={`${testId}-dot`}
          style={{ width: 8, height: 8, borderRadius: "50%", flexShrink: 0, background: dotColor }}
        />
        {isAllClear ? "Clear" : (
          <>
            {active.length}
            {chipLabel && (
              <span style={{ fontWeight: T.fwRegular }}>
                {" · "}{chipLabel}
              </span>
            )}
          </>
        )}
      </button>

      {onDismissAll && !isAllClear && (
        <button
          type="button"
          data-testid={`${testId}-dismiss-all`}
          aria-label="Dismiss all"
          onClick={(event) => {
            event.stopPropagation();
            handleDismissAll();
          }}
          style={{
            display:      "inline-flex",
            alignItems:   "center",
            justifyContent: "center",
            height:       T.toolbarCtrlH,
            boxSizing:    "border-box",
            padding:      "0 5px",
            border:       `1px solid var(--v2-line)`,
            borderLeft:   "none",
            background:   isAllClear ? T.ragGreenBg : T.card,
            borderRadius: "0 8px 8px 0",
            cursor:       "pointer",
            color:        T.mid,
            fontSize:     T.fsBase,
            lineHeight:   1,
          }}
        >
          ×
        </button>
      )}

      <div
        aria-hidden="true"
        data-testid={`${testId}-divider`}
        style={{ width: 1, height: 22, background: "var(--v2-line)", flexShrink: 0 }}
      />

      {open && !isAllClear && panelRect && createPortal(
        <div
          ref={panelRef}
          data-testid={`${testId}-panel`}
          style={{
            position:     "fixed",
            top:          panelRect.top,
            left:         panelRect.left,
            minWidth:     panelRect.minWidth,
            maxWidth:     420,
            zIndex:       1200,
            background:   T.white,
            border:       `1px solid ${T.border}`,
            borderRadius: 10,
            boxShadow:    "0 8px 28px rgba(16,36,58,.16)",
            padding:      "6px 0",
          }}
        >
          {panelHeader && (
            <div
              data-testid={`${testId}-panel-header`}
              style={{
                padding:      "9px 14px",
                fontSize:     T.fsSm5,
                fontWeight:   T.fwSemibold,
                color:        T.mid,
                borderBottom: `1px solid ${T.border}`,
              }}
            >
              {panelHeader}
            </div>
          )}
          {active.map((m) => (
            <div
              key={m.id}
              data-testid={`${testId}-row`}
              style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "9px 14px" }}
            >
              <span
                aria-hidden="true"
                style={{
                  width: 7, height: 7, marginTop: 5, borderRadius: "50%", flexShrink: 0,
                  background: SEVERITY_DOT[m.severity] ?? T.ragAmber,
                }}
              />
              <span style={{ flex: "1 1 auto", fontSize: T.fsSm5, color: T.dark }}>
                {m.text}
              </span>
              {m.actionLabel && (
                <button
                  type="button"
                  data-testid={`${testId}-action`}
                  onClick={m.onAction}
                  style={{
                    background:     "none",
                    border:         "none",
                    cursor:         "pointer",
                    color:          T.blue,
                    fontSize:       T.fsXs,
                    fontWeight:     T.fwSemibold,
                    textDecoration: "underline",
                    whiteSpace:     "nowrap",
                    padding:        0,
                  }}
                >
                  {m.actionLabel}
                </button>
              )}
              <button
                type="button"
                data-testid={`${testId}-dismiss`}
                aria-label="Dismiss"
                onClick={() => handleDismiss(m.id)}
                style={{
                  background: "none",
                  border:     "none",
                  cursor:     "pointer",
                  color:      T.mid,
                  fontSize:   T.fsBase,
                  lineHeight: 1,
                  padding:    "0 2px",
                }}
              >
                ×
              </button>
            </div>
          ))}
        </div>,
        document.body,
      )}
    </span>
  );
}
